/**
 * User Service
 *
 * Handles user profile and management API calls:
 * - Get user profile
 * - Update profile
 * - Avatar upload
 * - User preferences
 */

import { api } from '@/lib/api';
import { User } from './auth.service';
import type { CurrencyCode } from '@/lib/currency';

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  timezone?: string;
  language?: string;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  emailNotifications: boolean;
  pushNotifications: boolean;
  language: string;
  timezone: string;
}

export type UpdatePreferencesRequest = Partial<UserPreferences>;

/**
 * UserDto interface matching backend response structure
 * Returned by /auth/me and /auth/preferences endpoints
 */
export interface UserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  profilePictureUrl?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  twoFactorEnabled: boolean;
  accountStatus: string;
  verificationLevel: string;
  defaultTenantId?: string;
  timezone: string;
  locale: string;
  company?: string;
  country?: string;
  currency: string;  // ISO 4217 currency code (VND, USD, etc.)
  language: string;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * OAuth link information
 */
export interface OAuthLinkDto {
  provider: string;
  providerUserId: string;
  linkedAt: string;
}

/**
 * Active session information
 */
export interface SessionDto {
  id: string;
  deviceInfo?: string;
  ipAddress?: string;
  createdAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

/**
 * Current tenant information
 */
export interface TenantDto {
  id: string;
  name: string;
  slug: string;
}

/**
 * CurrentUserResponse wraps the UserDto from /auth/me endpoint
 */
export interface CurrentUserResponse {
  user: UserDto;
  oAuthLinks: OAuthLinkDto[];
  activeSessions: SessionDto[];
  currentTenant?: TenantDto;
}

export interface UploadAvatarResponse {
  avatarUrl: string;
}

/**
 * User Service Methods
 */
export const userService = {
  /**
   * Get current user profile
   */
  getProfile: (): Promise<User> => {
    return api.get('/users/profile');
  },

  /**
   * Update user profile
   */
  updateProfile: (data: UpdateProfileRequest): Promise<User> => {
    return api.patch('/users/profile', data);
  },

  /**
   * Upload user avatar
   */
  uploadAvatar: (file: File): Promise<UploadAvatarResponse> => {
    const formData = new FormData();
    formData.append('avatar', file);

    return api.post('/users/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Delete user avatar
   */
  deleteAvatar: (): Promise<void> => {
    return api.delete('/users/avatar');
  },

  /**
   * Get user preferences
   */
  getPreferences: (): Promise<UserPreferences> => {
    return api.get('/users/preferences');
  },

  /**
   * Update user preferences
   */
  updatePreferences: (data: UpdatePreferencesRequest): Promise<UserPreferences> => {
    return api.patch('/users/preferences', data);
  },

  /**
   * Get user by ID (admin only)
   */
  getUser: (userId: string): Promise<User> => {
    return api.get(`/users/${userId}`);
  },

  /**
   * List users (admin only)
   */
  listUsers: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
  }): Promise<{ users: User[]; total: number; page: number; limit: number }> => {
    const response = await api.getList<User>('/users', { params });
    return {
      users: response.data,
      total: response.pagination.totalItems,
      page: response.pagination.currentPage,
      limit: response.pagination.itemsPerPage
    };
  },

  /**
   * Get user's display currency preference
   *
   * Fetches the current user's currency preference from /auth/me endpoint.
   * The backend returns CurrentUserResponse with a UserDto containing the currency field.
   *
   * @returns Currency code (VND, USD, EUR, etc.)
   */
  getDisplayCurrency: async (): Promise<CurrencyCode> => {
    try {
      const response = await api.get<CurrentUserResponse>('/auth/me');
      return (response.user.currency as CurrencyCode) || 'VND';
    } catch {
      console.warn('Failed to fetch currency preference, defaulting to VND');
      return 'VND';
    }
  },

  /**
   * Update user's display currency preference
   *
   * Updates the currency using /auth/preferences endpoint.
   * Backend requires all preference fields (timezone, locale, language, currency).
   * This method fetches current preferences first, then updates with new currency.
   *
   * @param currency - Currency code (VND, USD, EUR, etc.)
   */
  updateDisplayCurrency: async (currency: CurrencyCode): Promise<void> => {
    try {
      // Fetch current user preferences first
      const currentUser = await api.get<CurrentUserResponse>('/auth/me');
      const user = currentUser.user;

      // Update preferences with new currency (backend requires all fields)
      await api.patch<UserDto>('/auth/preferences', {
        timezone: user.timezone,
        locale: user.locale,
        language: user.language,
        currency: currency
      });
    } catch (error) {
      console.error('Failed to update display currency:', error);
      throw error;
    }
  },

  /**
   * Get exchange rate from VND to target currency
   *
   * NOTE: This endpoint may not be implemented yet on the backend.
   * Waiting for Phase 4 backend implementation.
   *
   * @param fromCurrency - Source currency (should be VND)
   * @param toCurrency - Target currency
   */
  getExchangeRate: async (fromCurrency: string, toCurrency: string): Promise<{
    rate: number;
    lastUpdated: string;
  }> => {
    try {
      return await api.get('/exchange-rates', {
        params: { from: fromCurrency, to: toCurrency },
      });
    } catch (error) {
      console.error('Failed to fetch exchange rate:', error);
      throw error;
    }
  },
};
